import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TenderDetails } from "@/components/dashboard/TenderDetails";
import { TenderList } from "@/components/dashboard/TenderList";
import { Loader2, Trash2, Search, BookmarkPlus } from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export interface TenderMatch {
  id: string;
  tender_data?: {
    match_reasons?: {
      "notice-title"?: {
        eng?: string;
      };
      notice_title?: {
        eng?: string[];
      };
      "organisation-name"?: {
        eng?: string;
      };
    };
    organization_name?: string;
    deadline_date?: string;
    deadline?: string;
    value?: string;
    description?: string;
  };
  title?: string;
  organization?: string;
  value?: string;
  deadline?: string;
  category?: string;
  tenderType?: string;
  status?: string;
  originalLanguage?: string;
  translationStatus?: Record<string, string>;
  match_reasons?: Record<string, any>;
  organization_name?: string;
  submission_url?: string;
  publication_number?: string;
  tender_id?: string;
  description?: string;
  [key: string]: any; // Allow for dynamic fields from the API
}

const TenderAgent = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedTender, setSelectedTender] = useState<TenderMatch | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { t } = useTranslation("tenderAgent");
  const location = useLocation();
  const [isSearching, setIsSearching] = useState(false);
  const [hasPendingSearch, setHasPendingSearch] = useState(false);
  const [hasRunInitialSearch, setHasRunInitialSearch] = useState(false);
  
  // Get the latest profile data to ensure we have the most current preferences
  const { data: profile, isLoading: isLoadingProfile, refetch: refetchProfile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const { data: tenderMatches, isLoading, refetch } = useQuery({
    queryKey: ["tender-matches"],
    queryFn: async () => {
      // Get the latest profile data directly from the database
      // This ensures we always use the most up-to-date profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      
      const { data: latestProfile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
        
      if (profileError) throw profileError;
      
      console.log("Fetching tenders with profile:", JSON.stringify(latestProfile, null, 2));
      
      const { data, error } = await supabase.functions.invoke("fetch-tenders", {
        body: { profile: latestProfile },
      });

      if (error) throw error;
      
      console.log(`Received ${data?.length || 0} tenders from edge function`);
      
      return data;
    },
    enabled: false, // Don't run the query automatically
  });

  const handleRunAgent = async () => {
    if (isLoading || isSearching) return;

    try {
      setIsSearching(true);
      console.log("Starting manual agent run");
      
      toast({
        title: t("messages.runningAgent"),
        description: t("messages.runningDescription"),
      });
      
      // Refetch the profile to get latest changes
      await refetchProfile();
      
      // Directly capture the refetch result to ensure we have the latest data
      const { data: refreshedTenders } = await refetch();
      
      // Use the refreshed data to determine the toast message
      if (refreshedTenders && refreshedTenders.length > 0) {
        // Calculate how many tenders have passed deadlines
        const currentDate = new Date();
        console.log("Current date:", currentDate);
        
        const validTenders = refreshedTenders.filter(tender => {
          const deadlineStr = tender["deadline-receipt-tender-date-lot"]?.[0];
          if (!deadlineStr) return true;
          const deadlineDate = new Date(deadlineStr);
          return isNaN(deadlineDate.getTime()) || deadlineDate > currentDate;
        });
        
        const expiredCount = refreshedTenders.length - validTenders.length;
        
        toast({
          title: t("messages.success"),
          description: `${t("messages.foundTenders", { count: refreshedTenders.length })}${
            expiredCount > 0 ? ` ${t("messages.expiredTendersHidden", { count: expiredCount })}` : '.'
          }`,
        });
      } else {
        toast({
          title: t("messages.noMatchesFound"),
          description: t("messages.noMatchesDescription"),
        });
      }
    } catch (error) {
      console.error("Error running tender agent:", error);
      toast({
        title: t("messages.error"),
        description: t("messages.failedToRun"),
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
      setHasPendingSearch(false);
      setHasRunInitialSearch(true);
    }
  };

  const handleClearMatches = () => {
    queryClient.removeQueries({ queryKey: ["tender-matches"] });
    toast({
      title: t("messages.success"),
      description: t("messages.matchesCleared"),
    });
  };

  const handleTenderClick = (tender: TenderMatch) => {
    // Show the modal dialog with tender details
    setSelectedTender(tender);
    setIsDetailsOpen(true);
  };

  const handleSaveTender = async (tender: TenderMatch) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: t("common.error", "Error"),
          description: t("tenders.loginToSave", "You need to be logged in to save tenders"),
          variant: "destructive",
        });
        return;
      }
      
      // Prepare enhanced tender data with additional metadata
      const enhancedTenderData = {
        ...tender,
        title: tender["notice-title"] ? 
          (typeof tender["notice-title"] === 'object' ? 
            (tender["notice-title"].eng || Object.values(tender["notice-title"])[0]) : 
            tender["notice-title"]) : 
          tender.title || `Tender ${tender.id || tender.publication_number}`,
        organization_name: tender["buyer-name"] ? 
          (typeof tender["buyer-name"] === 'object' ? 
            (tender["buyer-name"].eng?.[0] || Object.values(tender["buyer-name"])[0]?.[0]) : 
            tender["buyer-name"]) : 
          tender.organization_name || "Organization not specified",
        id: tender.publication_number || tender.id  // Ensure the id is properly set in the tender_data
      };
      
      // Fix: Remove the tender_id from the insert object - it's a generated column
      const { error } = await supabase.from("saved_tenders").insert({
        user_id: user.id,
        tender_data: enhancedTenderData,
        status: "not_started",
      });
      
      if (error) {
        // Check if this is a duplicate tender error
        if (error.code === '23505') {
          toast({
            title: t("common.info", "Information"),
            description: t("tenders.alreadySaved", "This tender is already saved in your dashboard"),
          });
          return;
        }
        
        throw error;
      }
      
      toast({
        title: t("common.success"),
        description: t("messages.tenderSaved"),
      });

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["owned-tenders"] });
    } catch (error) {
      console.error("Error saving tender:", error);
      toast({
        title: t("common.error"),
        description: t("tenders.saveError", "Failed to save tender"),
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    // Determine if there's a navigation request to run the agent
    const state = location.state as { runAgentOnMount?: boolean };
    const shouldRunFromNavigation = state?.runAgentOnMount && !isSearching && !hasRunInitialSearch;
    
    if (shouldRunFromNavigation && profile) {
      console.log("Running agent on mount with profile:", profile);
      setHasPendingSearch(true);
      
      // Wait for the next render cycle to ensure all state is properly updated
      const timer = setTimeout(() => {
        handleRunAgent();
      }, 100);
      
      // Clear the state after running to prevent multiple executions
      window.history.replaceState({}, document.title);
      
      return () => clearTimeout(timer);
    }
  }, [location, profile, isSearching, hasRunInitialSearch]);

  return (
    <PageLayout 
      title={t("title")}
      description={t("description")}
      icon={<Search className="h-6 w-6" />}
    >
      <div className="container mx-auto py-8 space-y-8">
        <div className="flex justify-between items-center">
          <div className="space-x-4">
            <Button
              onClick={handleRunAgent}
              disabled={isLoading || isSearching || isLoadingProfile}
              className="flex items-center gap-2"
            >
              {(isLoading || isSearching) ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {t("buttons.runAgent")}
            </Button>
            <Button
              onClick={handleClearMatches}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {t("buttons.clearMatches")}
            </Button>
          </div>
        </div>

        {tenderMatches && tenderMatches.length > 0 && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <p className="text-green-800">
              {t("matchInfo.foundMatches", { count: tenderMatches.length })}
            </p>
          </div>
        )}

        <TenderList
          tenders={tenderMatches}
          isLoading={isLoading || isSearching || isLoadingProfile}
          onTenderClick={handleTenderClick}
          onSaveTender={handleSaveTender}
        />

        {selectedTender && (
          <TenderDetails
            tender={selectedTender}
            isOpen={isDetailsOpen}
            onOpenChange={setIsDetailsOpen}
            onSaveTender={() => handleSaveTender(selectedTender)}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default TenderAgent;
